import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLine, useLineMachines, type MachineDetail } from "@/features/lines/hooks";
import { MachineSidebar } from "@/features/machine-sidebar/MachineSidebar";
import { useRealtimeRooms } from "@/lib/use-realtime-rooms";
import { LineDetailHeader } from "@/features/lines/detail/components/LineDetailHeader";
import { LineOverviewTab } from "@/features/lines/detail/LineOverviewTab";
import { LineProductionReportTab } from "@/features/lines/detail/LineProductionReportTab";

// Spec 083 (T043) — o detalhe da linha, agora com DUAS ABAS.
//
// ⚠️ `Tabs` CONTROLADA, e não `defaultValue`. `TabsContent` renderiza os filhos sempre e só aplica
// `hidden`, então o relatório — a consulta mais cara da tela — dispararia em TODA visita ao detalhe
// da linha, para uma aba que a maioria não abre. Mesmo motivo do ciclo fechado.

export function LineDetailPage(): React.ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  // Entra no room desta linha para receber eventos ao vivo (room-per-line).
  useRealtimeRooms(id ? [id] : []);
  const {
    data: line,
    isLoading,
    isError: lineIsError,
    error: lineError,
    refetch: lineRefetch,
  } = useLine(id);
  const [selected, setSelected] = React.useState<MachineDetail | null>(null);
  const [tab, setTab] = React.useState("overview");

  // Mesma chave que a aba de visão geral consulta: TanStack dedupe, uma requisição só.
  const { data: machines } = useLineMachines(id);

  // Nav.machineDetail abre a linha com `?machine=<id>` ⇒ abre a sidebar da máquina.
  // Derivado (sem effect): o param abre até o usuário fechar (paramDismissed) ou clicar noutra.
  const [searchParams] = useSearchParams();
  const machineParam = searchParams.get("machine");
  // Dispensa é POR valor de param (não global): abrir outra máquina por deep-link volta a funcionar.
  const [dismissedParam, setDismissedParam] = React.useState<string | null>(null);
  const effectiveSelected =
    selected ??
    (machineParam && machineParam !== dismissedParam
      ? (machines?.find((x) => x.id === machineParam) ?? null)
      : null);

  if (isLoading) {
    return (
      <section className="space-y-6">
        <div className="bg-bg-muted h-9 w-72 rounded motion-safe:animate-pulse-dot" />
        <div className="bg-bg-muted h-24 rounded-lg motion-safe:animate-pulse-dot" />
      </section>
    );
  }

  if (lineIsError) {
    return (
      <Notify.QueryError
        error={lineError}
        onRetry={() => void lineRefetch()}
        title="Não foi possível carregar a linha"
      />
    );
  }

  if (!line) {
    return (
      <Card className="p-6">
        <p className="text-sm text-text-secondary">Linha não encontrada.</p>
      </Card>
    );
  }

  return (
    <section>
      <div>
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-2 text-[13px] text-text-secondary"
        >
          <Link
            to="/lines"
            className="inline-flex items-center gap-1 rounded text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Linhas
          </Link>
          <span aria-hidden="true" className="text-text-tertiary">
            /
          </span>
          <span className="font-mono text-text-tertiary" aria-label="ID da linha">
            {id}
          </span>
        </nav>
        <div className="mt-3">
          <LineDetailHeader lineId={id} />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList aria-label="Seções da linha">
          <TabsTrigger value="overview">Visão geral</TabsTrigger>
          <TabsTrigger value="report">Controle de produção</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6">
          <LineOverviewTab
            lineId={id}
            lineName={line.name}
            lineStatus={line.status}
            onSelectMachine={setSelected}
          />
        </TabsContent>
        <TabsContent value="report" className="mt-6">
          {tab === "report" ? <LineProductionReportTab lineId={id} /> : null}
        </TabsContent>
      </Tabs>

      {effectiveSelected ? (
        <MachineSidebar
          machineId={effectiveSelected.id}
          lineId={id}
          mode="edit"
          context="line-detail"
          onClose={() => {
            setSelected(null);
            setDismissedParam(machineParam);
          }}
        />
      ) : null}
    </section>
  );
}
